// Where to start (spec 31 / ATL-146): lobe geometry for the web's live
// recount while the handle drags (decision D6).
//
// MIRRORED COPY. The canonical implementation is
// packages/db/src/where-to-start.ts (used by the server ranking). Change both
// together: same signatures, same behaviour. The db test file
// (packages/db/src/where-to-start.test.ts) runs the same cases against this
// copy, so keep this file free of imports.

export type WhereToStartGeometryMode = "lobe" | "centred";

// Floating-point slack so a hill exactly on the rim counts as inside.
const RIM_EPSILON_M = 1e-6;

/** Lobe radius: a circle of circumference `loop` whose rim passes through the start. */
export function lobeRadiusM(loopKm: number): number {
  return (loopKm * 1000) / (2 * Math.PI);
}

/** Centred mode radius: a quarter of the loop around the start. */
export function centredRadiusM(loopKm: number): number {
  return (loopKm * 1000) / 4;
}

/** Bearing normalised into [0, 360). */
export function normaliseBearing(deg: number): number {
  const b = deg % 360;
  return b < 0 ? b + 360 : b;
}

/** Smallest angle between two bearings, 0..180. */
export function angularDifference(a: number, b: number): number {
  const d = Math.abs(normaliseBearing(a) - normaliseBearing(b));
  return d > 180 ? 360 - d : d;
}

/**
 * Is a point at distance `distanceM` and bearing `bearingDeg` from the start
 * inside the lobe of radius `radiusM` centred `radiusM` away along `thetaDeg`?
 * |P - C| <= r reduces to d <= 2r cos(b - theta) with cos(b - theta) > 0.
 * The start itself (d = 0) sits on the rim and counts as inside.
 */
export function insideLobe(
  distanceM: number,
  bearingDeg: number,
  radiusM: number,
  thetaDeg: number,
): boolean {
  if (distanceM <= RIM_EPSILON_M) return true;
  const cos = Math.cos(((normaliseBearing(bearingDeg) - normaliseBearing(thetaDeg)) * Math.PI) / 180);
  if (cos <= 0) return false;
  return distanceM <= 2 * radiusM * cos + RIM_EPSILON_M;
}

const COMPASS_16 = [
  "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW",
] as const;

const COMPASS_8_LONG = [
  "north", "north-east", "east", "south-east",
  "south", "south-west", "west", "north-west",
] as const;

/** 16-point short label ("E", "SSW"). Boundaries round up: 11.25 is NNE, 348.75 is N. */
export function compass16(bearingDeg: number): string {
  const i = Math.floor((normaliseBearing(bearingDeg) + 11.25) / 22.5) % 16;
  return COMPASS_16[i] ?? "N";
}

/** Long 8-point form for headlines: "to the east", "to the north-west". */
export function compass8Long(bearingDeg: number): string {
  const i = Math.floor((normaliseBearing(bearingDeg) + 22.5) / 45) % 8;
  return `to the ${COMPASS_8_LONG[i] ?? "north"}`;
}

function roundToHalfKm(m: number): number {
  return Math.max(500, Math.round(m / 500) * 500);
}

/**
 * Display edges of the three cumulative distance bands, rounded to the
 * nearest 0.5 km (never below 0.5 km). Lobe: 0.4, 0.7 and 1.0 of 2r (Day:
 * 2,000 / 3,500 / 5,000 m). Centred: a third, two thirds and all of loop / 4,
 * where `radiusM` is the centred radius. The last band counts everything
 * inside (true reach), so it always equals the headline count.
 */
export function bandEdges(radiusM: number, mode: WhereToStartGeometryMode): [number, number, number] {
  const reach = mode === "lobe" ? 2 * radiusM : radiusM;
  const f: [number, number, number] = mode === "lobe" ? [0.4, 0.7, 1] : [1 / 3, 2 / 3, 1];
  return [roundToHalfKm(f[0] * reach), roundToHalfKm(f[1] * reach), roundToHalfKm(f[2] * reach)];
}
